import { resolveLeadLocation } from "@/pages/Private/pages/Contacts/utils/resolveLeadLocation";

import { LeadLinkSource, buildReadyToSendHref } from "./buildReadyToSendHref";

/**
 * Where a per-lead "Review" affordance has to land, decided from the lead's own `assignStatus`.
 *
 * Every Messages surface draws from a fixed population, and they do not overlap:
 *   Review-to-send → DRAFT, SAVED_FOR_LATER, ARCHIVED  (`READY_TO_SEND_ASSIGN_STATUSES`)
 *   Outbox         → ASSIGNED, RUNNING, PAUSED, MESSAGE_SENT, ENDED, DONE (`OUTBOX_ASSIGN_STATUSES`)
 *   Inbox          → REPLIES
 * So "Review" is not one destination — it is a question about the lead, and {@link resolveLeadLocation}
 * already answers it for the Inbox/Outbox half. Review-to-send has no `LeadLocationKind` of its own
 * (a DRAFT lead resolves to `LIST`), so it is this function's fallback: the surface a lead awaiting
 * approval belongs to.
 *
 * PREC-4103 is what happens without it. The Outreach column linked *every* lead carrying a
 * `campaignId` to Review-to-send with `assignStatus=["ALL"]`, and that "ALL" expands to the three
 * statuses above — so a lead on the Assigned tab, which by definition holds none of them, could never
 * appear there. The user landed on a Review-to-send feed of unrelated leads. Production list 6542
 * (the one in the ticket) is 47 MESSAGE_SENT / 15 ENDED / 3 REPLIES / 2 DONE: not one row that page
 * could show.
 *
 * A name-search deep link into the Outbox is deliberately safe across its status tabs:
 * `useOutboxProspects` drops `outboxTabs`, `filter`, `senders` and `listIds` whenever `searchValue`
 * is set, so the lead surfaces even though the default **All** tab excludes SCHEDULED
 * (`DEFAULT_ALL_INCLUDE`). Pinning a tab here instead would reintroduce the same bug one surface over.
 *
 * Returns `undefined` when the prospect has no name to search by — same contract as
 * {@link buildReadyToSendHref}, since none of the three surfaces has a per-lead route.
 */
export const resolveLeadReviewTarget = (row: LeadLinkSource): string | undefined => {
	const prospectName = row.prospect?.name;

	if (!prospectName) {
		return undefined;
	}

	const location = resolveLeadLocation({
		assignStatus: row.assignStatus,
		listId: row.list?.id,
		listName: row.list?.name,
		prospectName,
	});

	if (location.kind === "INBOX" || location.kind === "OUTBOX") {
		return location.to;
	}

	return buildReadyToSendHref(row);
};
